import { Box, Stack, Link, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { JaaRoute, getRouterPathToContentIndex } from '../../utilities/Links';
import { useTranslation } from 'react-i18next';
import ArticleMeta from '../../types/ArticleMeta';
import dayjs from 'dayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ListOutlined } from '@mui/icons-material';
import NotificationsArea from './NotificationsArea';
import { CONTENT_TYPE } from '../../types/types';
import AuthorList from './AuthorList';
import SubscriberContentIcon from './SubscriberContentIcon';

interface ArticleHeaderProps {
  readonly meta: ArticleMeta;
  readonly restricted?: boolean;
  setIsInfoDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleTableOfContentsDrawer: (openDrawer: boolean) => () => void;
}

export default function ArticleHeader({
  meta,
  restricted = false,
  setIsInfoDrawerOpen,
  toggleTableOfContentsDrawer,
}: Readonly<ArticleHeaderProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <Stack
        direction='row'
        useFlexGap
        sx={{
          gap: 2,
          marginBottom: 3,
          width: '100%',

          ...(isMobile
            ? { flexWrap: 'noWrap', justifyContent: 'space-between' }
            : { flexWrap: 'wrap', justifyContent: 'flex-start' }),
        }}
      >
        {meta.type !== CONTENT_TYPE.LegalNews && (
          <Link
            component={RouterLink}
            to={JaaRoute.contentIndex}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              fontWeight: '600 !important',
              fontSize: '14px',
              textDecoration: 'none',
              color: 'text.secondary',
            }}
          >
            <ArrowBackIcon fontSize='inherit' sx={{ marginRight: 1 }} />
            {t('content.title')}
          </Link>
        )}

        {isMobile && meta.type !== CONTENT_TYPE.LegalNews && (
          <Box sx={{ marginLeft: 'auto' }}>
            <Button
              variant='outlined'
              startIcon={<InfoOutlinedIcon />}
              size='small'
              sx={{ marginRight: 1 }}
              onClick={(event) => {
                event.preventDefault();
                setIsInfoDrawerOpen(true);
              }}
            >
              {t('content.info')}
            </Button>
            {!restricted && (
              <Button
                variant='outlined'
                startIcon={<ListOutlined />}
                size='small'
                onClick={toggleTableOfContentsDrawer(true)}
              >
                {t('content.tableOfContents')}
              </Button>
            )}
          </Box>
        )}

        {!isMobile &&
          meta.allCategories().map((category) => {
            return (
              <Link
                to={getRouterPathToContentIndex(category)}
                component={RouterLink}
                key={category}
                underline='hover'
                variant='subtitle1'
                sx={{
                  color:
                    meta.categories.primary === category
                      ? 'primary.main'
                      : 'text.secondary',
                }}
              >
                {category}
              </Link>
            );
          })}
      </Stack>

      <NotificationsArea meta={meta} restricted={restricted} />

      {!isMobile && (
        <Stack
          direction='row'
          useFlexGap
          sx={{
            columnGap: 2,
            flexWrap: 'wrap',
            marginTop: 6,
          }}
        >
          {!meta.isPublic && (
            <Stack
              sx={{
                justifyContent: 'center',
              }}
            >
              <SubscriberContentIcon fontSize='small' />
            </Stack>
          )}
          <Typography
            component='span'
            variant='subtitle1'
            sx={{ color: 'text.secondary' }}
          >
            {meta.type}
          </Typography>

          {!meta.isUpcoming && (
            <Typography
              variant='subtitle1'
              sx={{
                paddingBottom: 0,
                flexShrink: 0,
                color: 'text.secondary',
              }}
            >
              {t('content.releasedOnDate', {
                date: dayjs(meta.releaseDate).format('D.M.YYYY'),
              })}
            </Typography>
          )}
          {meta.type !== CONTENT_TYPE.LegalNews && meta.authors.length > 0 && (
            <AuthorList
              variant='subtitle1'
              authors={meta.authors}
              TypographyProps={{
                sx: {
                  color: 'primary.main',
                  display: 'block',
                  flexWrap: 'nowrap',
                  flexShrink: 0,
                },
              }}
            />
          )}
        </Stack>
      )}
    </Box>
  );
}
